import { Box, Typography, useTheme } from "@mui/material";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { SegmentedButton, DataTableCard, M3Button } from "../components/m3";
import { AlignmentType, Document, HeadingLevel, ImageRun, Packer, Paragraph, Table, TableCell, TableRow, WidthType } from "docx";
import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../authentication/AuthProvider";
import { useContextProvider } from "../components/page-context/ContextProvider";
import config from "../config";
import { NetZeroTool } from "../db_access/net_zero_tool";
import { ShortTermTool } from "../db_access/short_term_tool";
import { Supply_chain } from "../db_access/supply_chain";
import { Structure } from "../db_access/structure";
import Grid from "../environment-page-components/Grid";
import { scope1Categories, scope2Categories, scope3Categories } from "../pages/Organization/ScopeCategory";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";
import "../style/components-style/NetZeroBottomTool.css";
import Scope12ShortTermTool from "./Scope1&2ShortTermTool";
import Scope3ShortTermTool from "./Scope3ShortTermTool";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function NetZeroBottomShortTermTool({
  selectedRow,
  page,
  yearlyRowData = [],
  yearlyColumnDefs = [],
  yearMapByColumnField = {},
  organizations = [],
  selectedOrganizationIds = [],
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const axisColors = isDark ? GRAPH_PALETTE.axes.darkMode : GRAPH_PALETTE.axes.lightMode;
  const {
    selectedProducts,
    setSelectedProducts,
    setSelectedProductIds,
    refresh,
    setRefresh,
    columnVisibilities,
    isSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    setIsSaveClicked,
    setIsSaveError,
    envFactorOptions,
  } = useContextProvider();
  const [shortTermToolVersion, setShortTermToolVersion] = useState("scope1&2");
  const [isOpen, setIsOpen] = useState(true);

  const [targetSettingMethod, setTargetSettingMethod] = useState("Absolute Contraction Approach");
  const [baseYear, setBaseYear] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [mostRecentYear, setMostRecentYear] = useState("");
  const [actionYear, setActionYear] = useState("");
  const isSupplyChainPage = page === "Supply Chain";
  const templateId = selectedRow?.id ?? null;

  const [scope1Emission, setScope1Emission] = useState(0.0);
  const [scope2Emission, setScope2Emission] = useState(0.0);
  const [scope3Emission, setScope3Emission] = useState(0.0);
  const [MRYScope1Emission, setMRYScope1Emission] = useState(0.0);
  const [MRYScope2Emission, setMRYScope2Emission] = useState(0.0);
  const [yearsList, setYearsList] = useState([]);
  const [scope1EmissionsList, setScope1EmissionsList] = useState([]);
  const [scope2EmissionsList, setScope2EmissionsList] = useState([]);
  const [scope1TargetEmission, setScope1TargetEmission] = useState(0.0);
  const [scope2TargetEmission, setScope2TargetEmission] = useState(0.0);
  const [yScaleType, setYScaleType] = useState("logarithmic");

  const [rowData, setRowData] = useState([]);
  const gridApiRef = useRef(null);

  const [baseYearOutput, setBaseYearOutput] = useState(0);
  const [targetYearOutput, setTargetYearOutput] = useState(0);
  const [economicIntensityBase, setEconomicIntensityBase] = useState(NaN);
  const [economicIntensityTarget, setEconomicIntensityTarget] = useState(NaN);
  const [economicIntensityReduction, setEconomicIntensityReduction] = useState(NaN);
  const [cementTargetPhysicalIntensity, setCementTargetPhysicalIntensity] = useState(NaN);

  const { user } = useAuth();
  const userId = user ? user.username : null;

  const shortTermGraphRef = useRef(null);

  const currentYear = new Date().getFullYear();

  // Extract all available years from yearlyColumnDefs and yearMapByColumnField
  const tableYears = useMemo(() => {
    const years = [];
    if (yearlyColumnDefs && yearlyColumnDefs.length > 0) {
      yearlyColumnDefs.forEach((colDef) => {
        if (colDef.field === "scope") return;
        let y = yearMapByColumnField?.[colDef.field];
        if (!y && colDef.headerName) {
          const match = colDef.headerName.match(/\b(20\d\d)\b/);
          if (match) y = match[1];
        }
        if (y && !years.includes(String(y))) {
          years.push(String(y));
        }
      });
    }
    return years;
  }, [yearlyColumnDefs, yearMapByColumnField]);

  const baseYearList = useMemo(() => {
    const defaultList = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => (2020 + i).toString());
    const combined = Array.from(new Set([...tableYears, ...defaultList]));
    return combined.sort();
  }, [tableYears, currentYear]);

  const mostRecentYearList = useMemo(() => {
    const defaultList = Array.from({ length: 5 }, (_, i) => (currentYear - 4 + i).toString());
    const combined = Array.from(new Set([...tableYears, ...defaultList]));
    return combined.sort();
  }, [tableYears, currentYear]);

  const targetYearList = Array.from({ length: 8 }, (_, i) => (2028 + i).toString());

  // Function to pull Scope 1, Scope 2, Scope 3 directly from the Yearly Based GHG table
  const getEmissionsForYear = useCallback((targetYear) => {
    if (!targetYear || !yearlyRowData || yearlyRowData.length === 0) return null;

    let targetField = null;
    if (yearMapByColumnField) {
      targetField = Object.keys(yearMapByColumnField).find(
        (f) => String(yearMapByColumnField[f]) === String(targetYear)
      );
    }
    if (!targetField && yearlyColumnDefs) {
      const foundDef = yearlyColumnDefs.find(
        (c) => c.headerName && c.headerName.includes(String(targetYear))
      );
      if (foundDef) targetField = foundDef.field;
    }
    if (!targetField) return null;

    const s1Row = yearlyRowData.find(
      (r) => String(r.scope || "").trim().toLowerCase() === "scope 1"
    );
    const s2Row = yearlyRowData.find(
      (r) => String(r.scope || "").trim().toLowerCase() === "scope 2"
    );
    const s3Rows = yearlyRowData.filter((r) => {
      const s = String(r.scope || "").trim().toLowerCase();
      return s.startsWith("scope 3") && !s.includes("total");
    });

    // In yearlyRowData, values are already stored in tons (tCO2e)
    const s1 = Number((Number(s1Row?.[targetField]) || 0).toFixed(2));
    const s2 = Number((Number(s2Row?.[targetField]) || 0).toFixed(2));
    const s3 = Number(s3Rows.reduce((sum, r) => sum + (Number(r[targetField]) || 0), 0).toFixed(2));

    return { s1, s2, s3 };
  }, [yearlyRowData, yearlyColumnDefs, yearMapByColumnField]);

  // Synchronize baseYear and mostRecentYear with the available table years
  useEffect(() => {
    if (tableYears.length > 0) {
      if (!baseYear || !tableYears.includes(baseYear)) {
        setBaseYear(tableYears[0]);
      }
      if (tableYears.length > 1) {
        if (!mostRecentYear || !tableYears.includes(mostRecentYear) || mostRecentYear === tableYears[0]) {
          setMostRecentYear(tableYears[tableYears.length - 1]);
        }
      }
    }
  }, [tableYears]);

  // Auto-pull Base Year emissions from the selected Base Year column
  useEffect(() => {
    if (!baseYear) return;
    const em = getEmissionsForYear(baseYear);
    if (em) {
      setScope1Emission(em.s1);
      setScope2Emission(em.s2);
      setScope3Emission(em.s3);
    }
  }, [baseYear, getEmissionsForYear]);

  // Auto-pull MRY emissions from the selected Most Recent Year column
  useEffect(() => {
    if (!mostRecentYear) return;
    const em = getEmissionsForYear(mostRecentYear);
    if (em) {
      setMRYScope1Emission(em.s1);
      setMRYScope2Emission(em.s2);
    }
  }, [mostRecentYear, getEmissionsForYear]);

  const actionYearList = useMemo(() => {
    if (!baseYear || !targetYear) return [];

    const start = parseInt(baseYear, 10);
    const end = parseInt(targetYear, 10);

    if (isNaN(start) || isNaN(end) || start >= end) return [];

    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [baseYear, targetYear]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
  }, []);

  const resetForm = () => {
    setBaseYear("");
    setTargetYear("");
    setMostRecentYear("");
    setScope1Emission(0.0);
    setScope2Emission(0.0);
    setMRYScope1Emission(0.0);
    setMRYScope2Emission(0.0);
  };
  useEffect(() => {
    if (!selectedRow || !selectedRow.id) {
      setRowData([]);
      return;
    }

    loadShortTermToolInputs();
    fetchData();
  }, [selectedRow]);

  useEffect(() => {
    if (!selectedRow || !selectedRow.id) return;
    // Only fetch raw emissions as a fallback if yearlyRowData is not available
    if (yearlyRowData && yearlyRowData.length > 0) return;

    const emissionFetch = page === "Supply Chain"
      ? Supply_chain.getEmissions(selectedRow.id)
      : Structure.getEmissions(selectedRow.id);
    emissionFetch
      .then((res) => res.json())
      .then((data) => {
        const s1Emissions = data.filter((d) => scope1Categories.includes(d.category));
        const s2Emissions = data.filter((d) => scope2Categories.includes(d.category));
        const s3Emissions = data.filter((d) => scope3Categories.includes(d.category));
        const s1_sum = s1Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        const s2_sum = s2Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        const s3_sum = s3Emissions.reduce((acc, curr) => acc + (parseFloat(curr.gwp) || 0), 0);
        setScope1Emission(s1_sum / 1000 || 0.0);
        setScope2Emission(s2_sum / 1000 || 0.0);
        setScope3Emission(s3_sum / 1000 || 0.0);
      });
  }, [page, selectedRow, yearlyRowData]);

  useEffect(() => {
    if (isSaveClicked) {
      saveShortTermToolInputs();
      saveTable();
      setIsSaveClicked(false);
      setIsLoading(false);
      setIsSaveVisible(false);
    }
  }, [isSaveClicked]);

  useEffect(() => {
    const startYear = parseInt(baseYear, 10);
    const endYear = parseInt(targetYear, 10);

    if (isNaN(startYear) || isNaN(endYear) || startYear >= endYear) {
      setScope1EmissionsList([]);
      setScope2EmissionsList([]);
      return;
    }

    const numYears = endYear - startYear + 1;
    const years = Array.from({ length: numYears }, (_, i) => startYear + i);

    const generateEmissions = (baseEmission) => {
      return Array.from({ length: numYears }, (_, i) => baseEmission * (1 - 0.042 * i));
    };

    const scope1Emissions = generateEmissions(scope1Emission);
    const scope2Emissions = generateEmissions(scope2Emission);

    setScope1EmissionsList(scope1Emissions);
    setScope2EmissionsList(scope2Emissions);
    setScope1TargetEmission(scope1Emissions.at(-1));
    setScope2TargetEmission(scope2Emissions.at(-1));
    setYearsList(years);
  }, [baseYear, targetYear, scope1Emission, scope2Emission]);

  const toggleContainer = () => setIsOpen(!isOpen);

  const fetchShortTermToolInputs = async (templateId) => {
    try {
      let inputData = null;
      if (page === "Supply Chain") {
        const response = await ShortTermTool.get_by_supply_chain_id(templateId);
        inputData = await response.json();
      } else {
        const response = await ShortTermTool.get_by_structure_id(templateId);
        inputData = await response.json();
      }
      if (!inputData || inputData.length === 0) {
        await addShortTermToolInputs(templateId);
      }

      return inputData;
    } catch (error) {
      console.error("Error fetching long-term tool inputs:", error);
      return null;
    }
  };

  const loadShortTermToolInputs = async () => {
    if (!selectedRow || !selectedRow.id) {
      resetForm();
      return;
    }

    const shortTermToolInputsData = await fetchShortTermToolInputs(selectedRow.id);

    if (shortTermToolInputsData && shortTermToolInputsData.length > 0) {
      const data = shortTermToolInputsData[0];

      setBaseYear(data.base_year || "");
      setTargetYear(data.target_year || "");
      setTargetSettingMethod(data.target_setting_method || "Absolute Contraction Approach");
      setMostRecentYear(data.most_recent_year || "");
      setMRYScope1Emission(data.mry_scope1 || 0.0);
      setMRYScope2Emission(data.mry_scope2 || 0.0);
      setBaseYearOutput(data.base_output ?? 0.0);
      setTargetYearOutput(data.target_output ?? 0.0);
    } else {
      resetForm();
    }
  };

  const addShortTermToolInputs = async (templateId) => {
    try {
      const stt = {
        supply_chain_id: null,
        user_id: userId,
        structure_id: null,
        corporate_id: null,
        base_year: null,
        target_year: null,
        target_setting_method: null,
        most_recent_year: null,
        my_scope1: null,
        my_scope2: null,
        base_output: null,
        target_output: null,
      };

      if (page === "Supply Chain") {
        stt.supply_chain_id = templateId;
      } else {
        stt.structure_id = templateId;
      }

      await ShortTermTool.new(stt);
    } catch (err) {
      console.error("Error creating short-term tool inputs:", err);
    }
  };

  const saveShortTermToolInputs = async () => {
    try {
      if (!selectedRow || !selectedRow.id) {
        console.warn("No template selected. Cannot update short-term tool inputs.");
        return;
      }

      const stt = {
        supply_chain_id: null,
        structure_id: null,
        corporate_id: null,
        user_id: userId,
        base_year: baseYear || null,
        target_year: targetYear || null,
        target_setting_method: targetSettingMethod || null,
        most_recent_year: mostRecentYear || null,
        scope1: scope1Emission || null,
        scope2: scope2Emission || null,
        mry_scope1: MRYScope1Emission || null,
        mry_scope2: MRYScope2Emission || null,
        base_output: baseYearOutput || null,
        target_output: targetYearOutput || null,
      };

      if (page === "Supply Chain") {
        stt.supply_chain_id = selectedRow.id;
      } else {
        stt.structure_id = selectedRow.id;
      }

      const result = page === "Supply Chain" ? await ShortTermTool.update_stt_by_supply_chain(stt) : await ShortTermTool.update_stt_by_structure(stt);

      if (result && result.status === 200) {
        const fresh = page === "Supply Chain" ? await ShortTermTool.get_by_supply_chain_id(selectedRow.id) : await ShortTermTool.get_by_structure_id(selectedRow.id);

        const data = await fresh.json();
        const first = data?.[0];

        if (first) {
          setBaseYear(first.base_year || "");
          setTargetYear(first.target_year || "");
          setMostRecentYear(first.most_recent_year || "");
          setTargetSettingMethod(first.target_setting_method || "Absolute Contraction Approach");
          setMRYScope1Emission(Number((first.mry_scope1 ?? 0).toFixed(2)));
          setMRYScope2Emission(Number((first.mry_scope2 ?? 0).toFixed(2)));
          setBaseYearOutput(first.base_output ?? 0);
          setTargetYearOutput(first.target_output ?? 0);
        }
        setIsSaveVisible(false);
      } else {
        console.error("Failed to save short-term tool inputs.");
        setIsSaveError(true);
      }
    } catch (error) {
      console.error("Error updating short-term tool inputs:", error);
      setIsSaveError(true);
    }
  };

  const fetchData = () => {
    if (!templateId) {
      setRowData([]);
      return;
    }

    const req = isSupplyChainPage ? NetZeroTool.get_by_supply_chain_id_and_type(templateId, "short-term") : NetZeroTool.get_by_structure_id_and_type(templateId, "short-term");

    req
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok: " + response.statusText);
        return response.json();
      })
      .then((data) => {
        setRowData(data || []);
      })
      .catch((error) => console.error("Error fetching data:", error));
  };

  const addRow = () => {
    if (!gridApiRef.current) return;

    if (!templateId) {
      window.alert("No template selected.");
      return;
    }

    if (actionYear === "") {
      window.alert("Select the year of the newly added action!");
      return;
    }

    const newRow = {
      id: undefined, // backend will create
      supply_chain_id: isSupplyChainPage ? templateId : null,
      structure_id: isSupplyChainPage ? null : templateId,
      corporate_id: null,
      type: "short-term",
      action: "",
      category: "",
      year: parseInt(actionYear, 10),
      details: "",
      estimated_ghg: 0,
    };

    NetZeroTool.new(newRow)
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok: " + response.statusText);
        return response.json();
      })
      .then(() => {
        // safest is to refetch from backend so IDs etc are correct
        fetchData();
      })
      .catch((error) => console.error("Error adding data:", error));
  };

  const deleteSelectedRows = () => {
    if (!gridApiRef.current) return;

    const selectedData = gridApiRef.current.getSelectedRows();
    NetZeroTool.delete_by_ids(selectedData.map((row) => row.id)).finally(() => fetchData());
  };

  const saveTable = () => {
    if (!gridApiRef.current) return;

    const allRows = gridApiRef.current.getRenderedNodes ? (gridApiRef.current.getRenderedNodes() || []).map((node) => node.data) : [];

    Promise.all(allRows.map((row) => NetZeroTool.update(row)))
      .then(() => {
        fetchData();
        setIsSaveVisible(false);
      })
      .catch((error) => console.error("Error updating data:", error));
  };

  const columnDefs = [
    { field: "id", hide: true },
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerName: t("Sl no."),
      field: "sno",
      width: 80,
      pinned: "left",
      valueGetter: (params) => params.node.rowIndex + 1,
      sortable: false,
      filter: false,
    },
    {
      headerName: t("Action"),
      field: "action",
      flex: 1,
      editable: true,
      cellEditor: "agTextCellEditor",
      filter: false,
    },
    {
      headerName: t("Term"),
      field: "type",
      flex: 1,
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Long-term", "Short-term", "Freight"],
      },
      filter: false,
    },
    {
      headerName: t("Category"),
      field: "category",
      flex: 1,
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Operational", "Strategic", "Tactical"],
      },
      filter: false,
    },
    {
      headerName: t("Year"),
      field: "year",
      flex: 1,
      editable: true,
      cellEditor: "agTextCellEditor",
      filter: false,
    },
    {
      headerName: t("Details"),
      field: "details",
      flex: 4,
      editable: true,
      cellEditor: "agTextCellEditor",
      filter: false,
    },
    {
      headerName: t("Estimated GHG reductions (ton CO₂e)"),
      field: "estimated_ghg",
      flex: 1,
      editable: true,
      cellStyle: { textAlign: "right" },
      cellEditor: "agTextCellEditor",
      cellEditorParams: {
        useFormatter: true,
        inputType: "number",
      },
      filter: false,
    },
  ];

  const freightToolGridProps = {
    columnDefs,
    rowData,
    rowSelection: "multiple",
    onGridReady,
    domLayout: "autoHeight",
    onCellValueChanged: ({ newValue, oldValue }) => {
      if (newValue !== oldValue) {
        setIsSaveVisible(true);
      }
    },
    gridOptions: {
      suppressCellSelection: false,
    },
  };

  const shortTermGraphOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,

    interaction: {
      mode: "index",
      intersect: false,
    },

    plugins: {
      datalabels: {
        display: false,
      },

      legend: {
        display: true,
        position: "top",
        align: "center",
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          boxHeight: 8,
          color: axisColors.tick,
          padding: 18,
          font: {
            size: 13,
            family: GRAPH_PALETTE.font.family,
            weight: "500",
          },
        },
      },

      tooltip: {
        enabled: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
        borderWidth: 1,
        titleColor: isDark ? "#ffffff" : "#0f172a",
        bodyColor: isDark ? "#e2e8f0" : "#334155",
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 4,
        titleFont: {
          size: 13,
          family: GRAPH_PALETTE.font.family,
          weight: "600",
        },
        bodyFont: {
          size: 12.5,
          family: GRAPH_PALETTE.font.family,
        },
        callbacks: {
          label: (context) => {
            const val = context.parsed.y;
            return val !== null && val !== undefined ? ` ${context.dataset.label}: ${val.toFixed(2)} tCO₂e` : "";
          },
        },
      },
    },

    layout: {
      padding: {
        left: 10,
        right: 20,
        top: 20,
        bottom: 10,
      },
    },

      scales: {
        y: {
          type: yScaleType === "logarithmic" ? "logarithmic" : "linear",
          beginAtZero: yScaleType !== "logarithmic",
          grid: {
            color: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
            drawBorder: false,
          },
          border: {
            display: true,
            color: axisColors.line,
            width: GRAPH_PALETTE.axes.strokeWidth,
          },
          title: {
            display: true,
            text: yScaleType === "logarithmic" ? `${t("Scope Emissions (tCO₂e)")} [${t("Log Scale")}]` : t("Scope Emissions (tCO₂e)"),
            color: axisColors.tick,
            font: {
              size: 12.5,
              family: GRAPH_PALETTE.font.family,
              weight: "600",
            },
            padding: { bottom: 8 },
          },
          ticks: {
            color: axisColors.tick,
            font: {
              size: 12,
              family: GRAPH_PALETTE.font.family,
            },
            padding: 8,
            callback: (value) => {
              if (yScaleType === "logarithmic") {
                const v = Number(value);
                if (v <= 0) return null;
                const exponent = Math.floor(Math.log10(v));
                const fraction = v / Math.pow(10, exponent);
                if (Math.abs(fraction - 1) < 1e-4 || Math.abs(fraction - 2) < 1e-4 || Math.abs(fraction - 5) < 1e-4) {
                  return v.toLocaleString();
                }
                return null;
              }
              return Number(value).toLocaleString();
            },
          },
        },

        x: {
          grid: {
            display: false,
          },
          border: {
            display: true,
            color: axisColors.line,
            width: GRAPH_PALETTE.axes.strokeWidth,
          },
          title: {
            display: true,
            text: t("Year"),
            color: axisColors.tick,
            font: {
              size: 12.5,
              family: GRAPH_PALETTE.font.family,
              weight: "600",
            },
            padding: { top: 8 },
          },
          ticks: {
            color: axisColors.tick,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            font: {
              size: 12,
              family: GRAPH_PALETTE.font.family,
            },
            padding: 6,
          },
        },
      },
    }), [t, isDark, axisColors, yScaleType]);

  const shortTermGraphData = useMemo(() => {
    const isLog = yScaleType === "logarithmic";
    const sanitizeVal = (val) => {
      const num = Number(val);
      if (isNaN(num) || num <= 0) return null;
      return num;
    };
    const s1Data = isLog ? scope1EmissionsList.map(sanitizeVal) : scope1EmissionsList;
    const s2Data = isLog ? scope2EmissionsList.map(sanitizeVal) : scope2EmissionsList;
    // Correctly map MRY points to the exact category position matching mostRecentYear
    const mryScope1Data = yearsList.map((y) => (String(y) === String(mostRecentYear) ? (isLog ? sanitizeVal(MRYScope1Emission) : MRYScope1Emission) : null));
    const mryScope2Data = yearsList.map((y) => (String(y) === String(mostRecentYear) ? (isLog ? sanitizeVal(MRYScope2Emission) : MRYScope2Emission) : null));

    return {
      labels: yearsList,
      datasets: [
        {
          label: t("Scope 1 Target Pathway"),
          data: s1Data,
          borderColor: GRAPH_PALETTE.scopes.Scope1,
          backgroundColor: GRAPH_PALETTE.scopes.Scope1,
          pointBorderColor: GRAPH_PALETTE.scopes.Scope1,
          pointBackgroundColor: "#ffffff",
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBorderWidth: 2,
          borderWidth: 2.5,
          tension: 0.25,
          fill: false,
          yAxisID: "y",
        },
        {
          label: t("Scope 2 Target Pathway"),
          data: s2Data,
          borderColor: GRAPH_PALETTE.scopes.Scope2,
          backgroundColor: GRAPH_PALETTE.scopes.Scope2,
          pointBorderColor: GRAPH_PALETTE.scopes.Scope2,
          pointBackgroundColor: "#ffffff",
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBorderWidth: 2,
          borderWidth: 2.5,
          tension: 0.25,
          fill: false,
          yAxisID: "y",
        },
        {
          label: t("Most Recent Year Scope 1"),
          data: mryScope1Data,
          backgroundColor: GRAPH_PALETTE.scopes.Scope1,
          borderColor: "#ffffff",
          borderWidth: 2,
          pointRadius: 7,
          pointHoverRadius: 9,
          pointBackgroundColor: GRAPH_PALETTE.scopes.Scope1,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          showLine: false,
          pointStyle: "circle",
          datalabels: { display: false },
          yAxisID: "y",
        },
        {
          label: t("Most Recent Year Scope 2"),
          data: mryScope2Data,
          backgroundColor: GRAPH_PALETTE.scopes.Scope2,
          borderColor: "#ffffff",
          borderWidth: 2,
          pointRadius: 7,
          pointHoverRadius: 9,
          pointBackgroundColor: GRAPH_PALETTE.scopes.Scope2,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          showLine: false,
          pointStyle: "circle",
          datalabels: { display: false },
          yAxisID: "y",
        },
      ],
    };
  }, [yearsList, scope1EmissionsList, scope2EmissionsList, mostRecentYear, MRYScope1Emission, MRYScope2Emission, t]);

  const generateTable = (data) => {
    return new Table({
      columnWidths: [4000, 6000],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      rows: data.map(
        (row) =>
          new TableRow({
            children: row.map(
              (cell) =>
                new TableCell({
                  children: [
                    new Paragraph({
                      text: cell != null ? String(cell) : "",
                      spacing: { after: 100 },
                    }),
                  ],
                })
            ),
          })
      ),
    });
  };

  const shortTermToolExport = async (shortTermGraphRef) => {
    try {
      const shortTermGraphUrl = await toPng(shortTermGraphRef.current, { quality: 1 });

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "Net Zero Short-Term Scope 1&2 & Scope 3 Tool Report",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),

              new Paragraph({
                text: "Input Data (Scope 1 & 2)",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["Target Setting Method", "Absolute Contraction Approach"],
                ["Base Year", baseYear ?? "N/A"],
                ["Base Year | Scope 1 emissions (tCO₂e)", (scope1Emission?.toFixed?.(2) ?? "0.00") + " tCO₂e"],
                ["Base Year | Scope 2 emissions (tCO₂e)", (scope2Emission?.toFixed?.(2) ?? "0.00") + " tCO₂e"],
                ["Target Year", targetYear ?? "N/A"],
                ["Most Recent Year", mostRecentYear ?? "N/A"],
                ["MRY | Scope 1 emissions (tCO₂e)", (MRYScope1Emission?.toFixed?.(2) ?? "0.00") + " tCO₂e"],
                ["MRY | Scope 2 emissions (tCO₂e)", (MRYScope2Emission?.toFixed?.(2) ?? "0.00") + " tCO₂e"],
              ]),

              new Paragraph({
                text: "Cross-Sector Absolute Reduction / Absolute Contraction Approach (ACA) (Scope 1 & 2)",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["", "Base Year", "Most Recent Year", "Target Year", "% Reduction to Date", "% SBT Reduction"],
                [
                  "Scope 1 emissions (t CO₂e)",
                  scope1Emission.toFixed(2),
                  MRYScope1Emission.toFixed(2),
                  scope1TargetEmission.toFixed(2),
                  `${((1 - MRYScope1Emission / scope1Emission) * 100).toFixed(2)}%`,
                  `${((yearsList.length - 1) * 0.042 * 100).toFixed(2)}%`,
                ],
                [
                  "Scope 2 emissions (t CO₂e)",
                  scope2Emission.toFixed(2),
                  MRYScope2Emission.toFixed(2),
                  scope2TargetEmission.toFixed(2),
                  `${((1 - MRYScope2Emission / scope2Emission) * 100).toFixed(2)}%`,
                  `${((yearsList.length - 1) * 0.042 * 100).toFixed(2)}%`,
                ],
                [
                  "Scope 1 + 2 emissions (t CO₂e)",
                  (scope1Emission + scope2Emission).toFixed(2),
                  (MRYScope1Emission + MRYScope2Emission).toFixed(2),
                  (scope1TargetEmission + scope2TargetEmission).toFixed(2),
                  `${((1 - (MRYScope1Emission + MRYScope2Emission) / (scope1Emission + scope2Emission)) * 100).toFixed(2)}%`,
                  `${((yearsList.length - 1) * 0.042 * 100).toFixed(2)}%`,
                ],
              ]),

              new Paragraph({
                text: "Input Data (Scope 3)",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["Target Setting Method", targetSettingMethod ?? "N/A"],
                ["Base Year", baseYear ?? "N/A"],
                ["Base Year | Scope 3 emissions (tCO₂e)", (scope3Emission?.toFixed?.(2) ?? "0.00") + " tCO₂e"],
                ["Target Year", targetYear ?? "N/A"],
                ...(targetSettingMethod === "Economic intensity" || targetSettingMethod === "Physical intensity" || targetSettingMethod === "Cement SDA 1.5ºC"
                  ? [["Base year output", baseYearOutput ?? "N/A"]]
                  : []),
                ...(targetSettingMethod === "Physical intensity" || targetSettingMethod === "Cement SDA 1.5ºC" ? [["Target year output", targetYearOutput ?? "N/A"]] : []),
              ]),

              new Paragraph({
                text: "Cross-Sector Absolute Reduction / Absolute Contraction Approach (ACA) (Scope 3)",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["", `Base year (${baseYear})`, `Target year (${targetYear})`, "% SBT Reduction"],
                ...(targetSettingMethod === "Absolute Contraction Approach"
                  ? [
                      [
                        "Absolute emissions - WB2C (t CO₂e)",
                        scope3Emission.toFixed(2),
                        (scope3Emission * (1 - 0.025 * (yearsList.length - 1))).toFixed(2),
                        `${((yearsList.length - 1) * 0.025 * 100).toFixed(2)}%`,
                      ],
                      [
                        "Absolute emissions - 1.5C (t CO₂e)",
                        scope3Emission.toFixed(2),
                        (scope3Emission * (1 - 0.042 * (yearsList.length - 1))).toFixed(2),
                        `${((yearsList.length - 1) * 0.042 * 100).toFixed(2)}%`,
                      ],
                    ]
                  : []),
                ...(targetSettingMethod === "Economic intensity"
                  ? [
                      [
                        "Economic intensity (tCO2/unit value added)",
                        economicIntensityBase.toFixed(2),
                        economicIntensityTarget.toFixed(2),
                        `${(economicIntensityReduction * 100).toFixed(2)}%`,
                      ],
                    ]
                  : []),
                ...(targetSettingMethod === "Physical intensity"
                  ? [
                      [
                        "Physical intensity (tCO2/custom physical unit)",
                        economicIntensityBase.toFixed(2),
                        economicIntensityTarget.toFixed(2),
                        `${(economicIntensityReduction * 100).toFixed(2)}%`,
                      ],
                    ]
                  : []),
                ...(targetSettingMethod === "Cement SDA 1.5ºC"
                  ? [
                      [
                        "Physical intensity (t CO₂e/t cement)",
                        economicIntensityBase.toFixed(2),
                        cementTargetPhysicalIntensity.toFixed(2),
                        `${((economicIntensityBase - cementTargetPhysicalIntensity) / economicIntensityBase).toFixed(2)}%`,
                      ],
                    ]
                  : []),
              ]),

              new Paragraph({
                text: "Actions Table",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
              }),

              generateTable([
                ["Sl no.", "Action", "Term", "Category", "Year", "Details", "ESTIMATED GHG REDUCTIONS (ton CO₂e)"],
                ...rowData.map((action, index) => [
                  String(index + 1),
                  action.action || "N/A",
                  action.type || "N/A",
                  action.category || "N/A",
                  action.year ?? "N/A",
                  action.details || "N/A",
                  action.estimated_ghg ?? "N/A",
                ]),
              ]),

              new Paragraph({
                text: "Short Term Emissions Graph",
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 100 },
              }),

              shortTermGraphUrl
                ? new Paragraph({
                    children: [
                      new ImageRun({
                        data: shortTermGraphUrl,
                        transformation: { width: 650, height: 300 },
                      }),
                    ],
                  })
                : new Paragraph({ text: "No short term emissions graph available." }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Short_Term_Tool_Report.docx");
    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert("An error occurred while generating the DOCX file.");
    }
  };

  return (
    <div className="absolute-target-section">
      <div className="net-zero-tool-header export-ignore">
        <h2 onClick={toggleContainer}>Net Zero Short Term Tool</h2>
      </div>

      {isOpen && (
        <div className="tool-section">
          <Box sx={{ mb: 3 }} className="export-ignore">
            <SegmentedButton
              options={[
                { value: "scope1&2", label: t("Scope 1 & 2") },
                { value: "scope3", label: t("Scope 3") },
              ]}
              value={shortTermToolVersion}
              onChange={setShortTermToolVersion}
            />
          </Box>

          {shortTermToolVersion === "scope1&2" ? (
            <Scope12ShortTermTool
              resetForm={resetForm}
              saveShortTermToolInputs={saveShortTermToolInputs}
              baseYear={baseYear}
              setBaseYear={setBaseYear}
              baseYearList={baseYearList}
              targetYear={targetYear}
              setTargetYear={setTargetYear}
              targetYearList={targetYearList}
              mostRecentYear={mostRecentYear}
              setMostRecentYear={setMostRecentYear}
              mostRecentYearList={mostRecentYearList}
              scope1Emission={scope1Emission}
              setScope1Emission={setScope1Emission}
              scope2Emission={scope2Emission}
              setScope2Emission={setScope2Emission}
              MRYScope1Emission={MRYScope1Emission}
              setMRYScope1Emission={setMRYScope1Emission}
              MRYScope2Emission={MRYScope2Emission}
              setMRYScope2Emission={setMRYScope2Emission}
              scope1TargetEmission={scope1TargetEmission}
              scope2TargetEmission={scope2TargetEmission}
              yearsList={yearsList}
              shortTermGraphRef={shortTermGraphRef}
              shortTermGraphData={shortTermGraphData}
              shortTermGraphOptions={shortTermGraphOptions}
              yScaleType={yScaleType}
              setYScaleType={setYScaleType}
            />
          ) : (
            <Scope3ShortTermTool
              resetForm={resetForm}
              saveShortTermToolInputs={saveShortTermToolInputs}
              targetSettingMethod={targetSettingMethod}
              setTargetSettingMethod={setTargetSettingMethod}
              baseYear={baseYear}
              setBaseYear={setBaseYear}
              baseYearList={baseYearList}
              targetYear={targetYear}
              setTargetYear={setTargetYear}
              targetYearList={targetYearList}
              scope3Emission={scope3Emission}
              yearsList={yearsList}
              baseYearOutput={baseYearOutput}
              setBaseYearOutput={setBaseYearOutput}
              targetYearOutput={targetYearOutput}
              setTargetYearOutput={setTargetYearOutput}
              economicIntensityBase={economicIntensityBase}
              setEconomicIntensityBase={setEconomicIntensityBase}
              economicIntensityTarget={economicIntensityTarget}
              setEconomicIntensityTarget={setEconomicIntensityTarget}
              economicIntensityReduction={economicIntensityReduction}
              setEconomicIntensityReduction={setEconomicIntensityReduction}
              cementTargetPhysicalIntensity={cementTargetPhysicalIntensity}
              setCementTargetPhysicalIntensity={setCementTargetPhysicalIntensity}
            />
          )}

          <Box sx={{ mt: 3 }} className="export-ignore">
            <DataTableCard
              title={t("Action Planning")}
              actions={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <M3Button variant="tonal" size="sm" onClick={addRow} icon={<FiPlus size={16} />}>
                    {t("Add row")}
                  </M3Button>
                  <M3Button variant="outlined" size="sm" onClick={deleteSelectedRows} icon={<FiTrash2 size={16} />}>
                    {t("Delete selected rows")}
                  </M3Button>
                  <Box
                    component="select"
                    value={actionYear}
                    onChange={(e) => setActionYear(e.target.value)}
                    sx={{
                      height: "36px",
                      px: 1.5,
                      borderRadius: "8px",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--m3-on-surface, #1b1c1a)",
                      bgcolor: "var(--m3-surface, #ffffff)",
                      border: "1px solid var(--m3-outline-variant, #e5e7eb)",
                      outline: "none",
                      cursor: "pointer",
                      "&:focus": {
                        borderColor: "var(--m3-primary, #659d33)",
                        boxShadow: "0 0 0 2px rgba(101, 157, 51, 0.2)",
                      },
                    }}
                  >
                    <option value="">{t("Select Action Year")}</option>
                    {actionYearList.map((year, index) => (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    * {t("Select action year between base year and target year")}
                  </Typography>
                </Box>
              }
            >
              <Grid {...freightToolGridProps} />
            </DataTableCard>
          </Box>
        </div>
      )}
    </div>
  );
}

export default NetZeroBottomShortTermTool;
